/**
 * GolferConfig Component
 *
 * Provides controls for configuring a golfer's handicap, course selection,
 * and consistency settings for simulation.
 */

import { useState } from 'react';
import {
    COURSE_PRESETS,
    getHandicapLabel,
    getConsistencyLabel
} from '../utility/golfConfig';
import { calculateCourseHandicap, calculateExpectedScore } from '../utility/golfSimulation';

/**
 * Collapsible section component
 */
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="border border-gray-200 rounded-lg mb-4">
            <button
                className="w-full px-4 py-3 text-left font-semibold bg-gray-50 hover:bg-gray-100 rounded-t-lg flex justify-between items-center"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span>{title}</span>
                <span className="text-gray-500">{isOpen ? '−' : '+'}</span>
            </button>
            {isOpen && (
                <div className="p-4 bg-white rounded-b-lg">
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Handicap slider with visual indicators
 */
const HandicapSlider = ({ value, onChange }) => {
    const markers = [
        { value: 0, label: 'Scratch' },
        { value: 10, label: 'Mid' },
        { value: 20, label: 'High' },
        { value: 36, label: 'Beginner' }
    ];

    return (
        <div className="mb-6">
            <div className="flex justify-between items-baseline mb-2">
                <label className="block font-bold text-sm sm:text-base">
                    Handicap Index
                </label>
                <span className="text-lg font-semibold text-green-700">
                    {value} <span className="text-sm font-normal text-gray-500">({getHandicapLabel(value)})</span>
                </span>
            </div>

            <input
                type="range"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                min="0"
                max="36"
                step="1"
                value={value}
                onChange={(e) => onChange(parseInt(e.target.value))}
            />

            <div className="flex justify-between text-xs text-gray-500 mt-1 px-1">
                {markers.map(marker => (
                    <span key={marker.value} className="text-center" style={{ width: '60px' }}>
                        {marker.label}
                    </span>
                ))}
            </div>
        </div>
    );
};

/**
 * Course difficulty selector
 */
const CourseSelector = ({ course, onChange, customCourse, onCustomChange }) => {
    const [showCustom, setShowCustom] = useState(false);

    const handlePresetChange = (presetId) => {
        if (presetId === 'custom') {
            setShowCustom(true);
            onChange(customCourse);
        } else {
            setShowCustom(false);
            onChange(COURSE_PRESETS[presetId]);
        }
    };

    const handleCustomFieldChange = (field, value) => {
        const updated = { ...customCourse, [field]: parseFloat(value) };
        onCustomChange(updated);
        onChange(updated);
    };

    const currentPresetId = Object.keys(COURSE_PRESETS).find(
        key => COURSE_PRESETS[key].courseRating === course.courseRating &&
               COURSE_PRESETS[key].slopeRating === course.slopeRating
    ) || 'custom';

    return (
        <div>
            <label className="block font-bold text-sm sm:text-base mb-2">
                Course Difficulty
            </label>

            <div className="flex gap-2 mb-4 flex-wrap">
                {Object.entries(COURSE_PRESETS).map(([id, preset]) => (
                    <button
                        key={id}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colours ${
                            currentPresetId === id && !showCustom
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        onClick={() => handlePresetChange(id)}
                    >
                        {preset.name}
                    </button>
                ))}
                <button
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colours ${
                        showCustom
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handlePresetChange('custom')}
                >
                    Custom
                </button>
            </div>

            {/* Course info */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm mb-4">
                <div className="font-medium mb-1">{course.name}</div>
                <div className="text-gray-600 mb-2">{course.description}</div>
                <div className="flex gap-4 text-gray-700">
                    <span>Par: <strong>{course.par}</strong></span>
                    <span>Rating: <strong>{course.courseRating}</strong></span>
                    <span>Slope: <strong>{course.slopeRating}</strong></span>
                </div>
            </div>

            {/* Custom inputs */}
            {showCustom && (
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Par</label>
                        <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            value={customCourse.par}
                            onChange={(e) => handleCustomFieldChange('par', e.target.value)}
                            min="60"
                            max="80"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Course Rating</label>
                        <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            value={customCourse.courseRating}
                            onChange={(e) => handleCustomFieldChange('courseRating', e.target.value)}
                            min="60"
                            max="80"
                            step="0.1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Slope Rating</label>
                        <input
                            type="number"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            value={customCourse.slopeRating}
                            onChange={(e) => handleCustomFieldChange('slopeRating', e.target.value)}
                            min="55"
                            max="155"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Consistency slider
 */
const ConsistencySlider = ({ value, onChange }) => {
    return (
        <div>
            <div className="flex justify-between items-baseline mb-2">
                <label className="block font-bold text-sm sm:text-base">
                    Consistency
                </label>
                <span className="text-sm text-gray-600">
                    {getConsistencyLabel(value)}
                </span>
            </div>

            <input
                type="range"
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
                min="0.15"
                max="0.35"
                step="0.01"
                value={value}
                onChange={(e) => onChange(parseFloat(e.target.value))}
            />

            <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Very Consistent</span>
                <span>Erratic</span>
            </div>

            <p className="text-xs text-gray-500 mt-2">
                Affects how much scores vary from round to round. More consistent golfers
                have tighter score distributions around their expected score.
            </p>
        </div>
    );
};

/**
 * Expected score display
 */
const ExpectedScoreDisplay = ({ handicap, course }) => {
    const courseHandicap = calculateCourseHandicap(
        handicap,
        course.slopeRating,
        course.courseRating,
        course.par
    );
    const expectedScore = calculateExpectedScore(handicap, course);
    const relativeToPar = expectedScore - course.par;
    const relativeDisplay = relativeToPar > 0 ? `+${relativeToPar}` : relativeToPar;

    return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
            <div className="text-center">
                <div className="text-sm text-green-700 mb-1">Expected Score</div>
                <div className="text-3xl font-bold text-green-800">
                    {expectedScore}
                    <span className="text-lg ml-2">({relativeDisplay})</span>
                </div>
                <div className="text-xs text-green-600 mt-2">
                    Course Handicap: {courseHandicap}
                </div>
            </div>
        </div>
    );
};

/**
 * Main GolferConfig component
 */
export const GolferConfig = ({
    handicap,
    onHandicapChange,
    course,
    onCourseChange,
    consistency,
    onConsistencyChange
}) => {
    const [customCourse, setCustomCourse] = useState({
        id: 'custom',
        name: 'Custom Course',
        description: 'Your custom course configuration',
        par: 72,
        courseRating: 72.0,
        slopeRating: 125
    });

    return (
        <div className="mb-6">
            {/* Primary control: Handicap */}
            <HandicapSlider
                value={handicap}
                onChange={onHandicapChange}
            />

            {/* Course selection (collapsible) */}
            <CollapsibleSection title="Course Settings" defaultOpen={false}>
                <CourseSelector
                    course={course}
                    onChange={onCourseChange}
                    customCourse={customCourse}
                    onCustomChange={setCustomCourse}
                />
            </CollapsibleSection>

            {/* Advanced: Consistency (collapsible) */}
            <CollapsibleSection title="Advanced Settings" defaultOpen={false}>
                <ConsistencySlider
                    value={consistency}
                    onChange={onConsistencyChange}
                />
            </CollapsibleSection>

            {/* Expected score display */}
            <ExpectedScoreDisplay
                handicap={handicap}
                course={course}
            />
        </div>
    );
};
